import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Instagram, Play } from 'lucide-react';
import { Button } from './ui/button';

const Gallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const galleryItems = [
    {
      type: 'image',
      url: 'https://static.wixstatic.com/media/509f32_1dfa075470d548d397aeb4291414d784~mv2.png/v1/fill/w_650,h_790,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/509f32_1dfa075470d548d397aeb4291414d784~mv2.png',
      alt: 'Long blonde balayage hair transformation at Prép Beauty Parlour Vancouver',
    },
    {
      type: 'video',
      url: 'https://customer-assets.emergentagent.com/job_luxury-locks-4/artifacts/aurnx4w0_Hair%20colour%20Prep%20Beauty.mp4',
      alt: 'Hair colour transformation process at Prép Beauty Parlour Vancouver',
      title: 'Professional Hair Colour Service - Prép Beauty Parlour',
    },
    {
      type: 'image',
      url: 'https://static.wixstatic.com/media/509f32_c89d598762074ee79fff7c6dfc9f9f02~mv2.jpeg/v1/fill/w_953,h_1172,fp_0.50_0.50,q_85,usm_0.66_1.00_0.01,enc_auto/Balayage-vancouver.jpeg',
      alt: 'Beautiful wavy balayage hair styling result in Vancouver',
    },
    {
      type: 'video',
      url: 'https://customer-assets.emergentagent.com/job_luxury-locks-4/artifacts/obcsqvj0_Balayage%20hair%20colour%20vancouver.mov',
      alt: 'Balayage hair colour technique demonstration in Vancouver',
      title: 'Expert Balayage Application - Vancouver Hair Salon',
    },
    {
      type: 'video',
      url: 'https://customer-assets.emergentagent.com/job_luxury-locks-4/artifacts/qtf3x0pi_Hair%20Prep%20Beauty.mp4',
      alt: 'Hair styling and finishing at Prép Beauty Parlour',
      title: 'Professional Hair Styling Service - Prép Beauty',
    },
    {
      type: 'video',
      url: 'https://customer-assets.emergentagent.com/job_luxury-locks-4/artifacts/e7m1giqb_Pre%CC%81p%20Beauty-expert-besst-balayage-Vancouver.mov',
      alt: 'Expert balayage transformation by best Vancouver hair salon',
      title: 'Best Balayage Vancouver - Prép Beauty Parlour Expert Technique',
    },
    {
      type: 'video',
      url: 'https://customer-assets.emergentagent.com/job_luxury-locks-4/artifacts/7vc5bvgn_Best-downtown-salon-balayage-vancouver.mov',
      alt: 'Downtown Vancouver best salon balayage hair coloring service',
      title: 'Best Downtown Salon Balayage - Vancouver Hair Colour Specialists',
    },
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryItems.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  const currentItem = galleryItems[currentIndex];

  return (
    <section id="gallery" className="section-padding bg-gradient-to-b from-black via-[#0f0f0f] to-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-[#c9a961] text-sm font-semibold tracking-[0.2em] uppercase">Portfolio</span>
          </div>
          <h2 className="font-['Playfair_Display'] text-5xl md:text-6xl font-light text-[#f8f6f0] mb-6">
            Our Balayage
            <span className="block text-[#c9a961]">Transformations</span>
          </h2>
          <p className="text-[#f8f6f0]/70 text-lg max-w-2xl mx-auto">
            Explore recent balayage transformations from our Downtown Vancouver salon. Each result is customized based on hair type, colour history, and desired maintenance level.
          </p>
        </div>

        {/* Main Gallery Carousel */}
        <div className="relative max-w-5xl mx-auto mb-12">
          <div className="relative aspect-[4/5] overflow-hidden border border-[#c9a961]/30 shadow-2xl bg-black">
            {currentItem.type === 'image' ? (
              <img
                src={currentItem.url}
                alt={currentItem.alt}
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                key={currentItem.url}
                src={currentItem.url}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                aria-label={currentItem.alt}
                title={currentItem.title}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            
            {/* Media Type Indicator */}
            {currentItem.type === 'video' && (
              <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-sm border border-[#c9a961]/30 px-3 py-1.5 flex items-center space-x-2">
                <Play size={14} className="text-[#c9a961]" fill="#c9a961" />
                <span className="text-[#f8f6f0] text-xs font-medium">VIDEO</span>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <button
            data-testid="gallery-prev"
            aria-label="Previous gallery item"
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/80 backdrop-blur-sm border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] hover:bg-[#c9a961] hover:text-black transition-all duration-300 hover:scale-110"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            data-testid="gallery-next"
            aria-label="Next gallery item"
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/80 backdrop-blur-sm border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] hover:bg-[#c9a961] hover:text-black transition-all duration-300 hover:scale-110"
          >
            <ChevronRight size={24} />
          </button>

          {/* Indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
            {galleryItems.map((_, index) => (
              <button
                key={index}
                data-testid={`gallery-indicator-${index}`}
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-[#c9a961] w-8'
                    : 'bg-[#f8f6f0]/30 hover:bg-[#f8f6f0]/50 w-2'
                }`}
              />
            ))}
          </div>

          {/* Counter */}
          <div data-testid="gallery-counter" className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm border border-[#c9a961]/30 px-3 py-1.5">
            <span className="text-[#f8f6f0] text-sm font-medium">
              {currentIndex + 1} / {galleryItems.length}
            </span>
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center p-6 bg-gradient-to-br from-[#1a1a1a] to-black border border-[#c9a961]/20">
            <div className="text-[#c9a961] text-2xl mb-2">✨</div>
            <div className="text-[#f8f6f0] font-medium mb-1">Real Clients</div>
            <div className="text-[#f8f6f0]/60 text-sm">Authentic transformations</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#1a1a1a] to-black border border-[#c9a961]/20">
            <div className="text-[#c9a961] text-2xl mb-2">🎨</div>
            <div className="text-[#f8f6f0] font-medium mb-1">Custom Colour</div>
            <div className="text-[#f8f6f0]/60 text-sm">Personalized placement</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#1a1a1a] to-black border border-[#c9a961]/20">
            <div className="text-[#c9a961] text-2xl mb-2">💎</div>
            <div className="text-[#f8f6f0] font-medium mb-1">Natural Grow-Out</div>
            <div className="text-[#f8f6f0]/60 text-sm">Low-maintenance beauty</div>
          </div>
        </div>

        {/* Instagram CTA */}
        <div className="text-center bg-gradient-to-r from-transparent via-[#c9a961]/10 to-transparent py-12">
          <Instagram className="text-[#c9a961] mx-auto mb-4" size={40} />
          <h3 className="font-['Playfair_Display'] text-3xl text-[#f8f6f0] mb-3">
            See More on Instagram
          </h3>
          <p className="text-[#f8f6f0]/70 mb-6">Follow @prepbeautyparlour for daily inspiration</p>
          <Button
            data-testid="gallery-instagram-cta"
            onClick={() => window.open('https://www.instagram.com/prepbeautyparlour', '_blank')}
            variant="outline"
            className="border-2 border-[#c9a961] text-[#c9a961] hover:bg-[#c9a961] hover:text-black font-semibold px-8 py-3 rounded-none transition-all duration-300"
          >
            View Instagram Gallery
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
